import React, { useState, useRef, useEffect } from "react";

import {
  Box,
  Grid,
  Button,
  CircularProgress,
  Snackbar,
  Alert,
  Tab,
  Tabs,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EmailIcon from "@mui/icons-material/Email";
import { useNavigate, useParams } from "react-router-dom";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import PrintIcon from '@mui/icons-material/Print';
import AddWorkOrderSmart from "./components/addWorkorder"; // adjust path if needed


// Shared components
import CommentsTab from "./components/CommentsTab";
import CommentModal from "./components/CommentModal";
import WorkOrderTimeline from "./components/WorkOrderTimeline";
import MemberProfileCard from "./components/MemberProfileCard";
import DetailsTab from "./components/DetailsTab";
import CalendarTab from "./components/CalendarTab";
import VisitsTab from "./components/VisitsTab";
import ImageModal from "./components/ImageModal";
import WorkOrderPrintContent from "./components/WorkOrderPrintContent";
import "./components/WorkOrderPrintStyles.css";

// Hooks and API
import useWorkOrderDetails from "./hooks/useWorkOrderDetails";
import api, { API_BASE_URL } from "../../utils/api";

const WorkOrdersDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const storedUser = localStorage.getItem("user");
let currentUserId = null;

try {
  if (storedUser) {
    const parsed = JSON.parse(storedUser);
    currentUserId = parsed.userId;
  }
} catch (err) {
  console.error("❌ Failed to parse user from localStorage:", err);
}


  const printRef = useRef();

  const {
    workOrderDetails,
    member,
    setMember,
    comments,
    statuses,
    users,
    priorityLevels,
    assignableUsers,
    workOrderTypes,
    workOrderSubtypes,
    workOrderLocations,
    selectedTypeId,
    selectedSubtypeId,
    selectedLocationId,
    selectedPriority,
    selectedAssignee,
    setSelectedTypeId,
    setSelectedSubtypeId,
    setSelectedLocationId,
    setSelectedPriority,
    setSelectedAssignee,
    setWorkOrderDetails,
    setComments,
    setEvents,
    fetchComments,
    handleTypeChange,
    handleAssigneeChange,
    handlePriorityChange,
    handleSubtypeChange,
    handleLocationChange,
    calculateDaysOpen,
    imageURL,
    isLoading,
    error,
  } = useWorkOrderDetails(id, currentUserId);


  

  // 🔄 Called when the property (member) changes from DetailsTab
const handlePropertyChanged = async (newPropertyId) => {
  try {
    console.log("🔁 Refresh triggered after property change:", newPropertyId);

    // 1️⃣ Reload work order details
    await api.get(`/workorder/${id}`);

    // 2️⃣ Refresh comments
    await fetchComments();

    // 3️⃣ Re-fetch unresolved tickets
    const memberWorkOrdersResp = await api.get(`/workorders/member/${newPropertyId}`);
    const unresolvedData = memberWorkOrdersResp.data.filter(
      (wo) => wo.status_id !== 4
    );
    setUnresolvedTickets(unresolvedData);

    // 4️⃣ Re-fetch member info
    try {
  setMember(null); // ✅ clear old data so React knows it's a new member
  const memberResponse = await api.get(`/members/${newPropertyId}`);
  if (memberResponse.data) {
    setMember(memberResponse.data);
  }
} catch (memberErr) {
  console.error("⚠️ Failed to refresh member info:", memberErr);
}

    setSnackbarMessage("✅ Refreshed data for new property");
    setSnackbarOpen(true);
  } catch (err) {
    console.error("❌ Failed to refresh after property change:", err);
    setSnackbarMessage("❌ Failed to refresh data");
    setSnackbarOpen(true);
  }
};




  const [isCommentModalOpen, setIsCommentModalOpen] = useState(false);
  const [newComment, setNewComment] = useState("");
  const [commentType, setCommentType] = useState("external");
  const [selectedFile, setSelectedFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [recipientEmail, setRecipientEmail] = useState("");
  const [ccEmail, setCcEmail] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [commentImageURL, setCommentImageURL] = useState(null);
  const [openCommentImageModal, setOpenCommentImageModal] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [activeTab, setActiveTab] = useState(0);
  const [unresolvedTickets, setUnresolvedTickets] = useState([]);
  const [openNewTicketModal, setOpenNewTicketModal] = useState(false);
  const [availableProperties, setAvailableProperties] = useState([]);
  

 
  const handleAddComment = async () => {
  if (!newComment.trim()) return alert("Comment cannot be empty.");

  try {
    const formData = new FormData();
    formData.append("work_order_id", id);
    formData.append("user_id", currentUserId);
    formData.append("comment_text", newComment);
    formData.append("is_internal", commentType === "internal" ? 1 : 0);
    if (selectedFile) formData.append("upload_urls", selectedFile);

    // Save comment
    const { data } = await api.post("/addComment", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });

    // ⚡ Immediately show locally
    setComments(prev => [
      {
        comment_id: data.comment_id,
        comment_text: newComment,
        sent_at: data.sent_at || new Date().toISOString(),
        is_internal: commentType === "internal" ? 1 : 0,
        user_name: "You",
        user_lastname: "",
      },
      ...prev,
    ]);

    // 🔄 Then refresh from server for synced data (attachments, etc.)
    await fetchComments();

    // ✅ Reset fields
    setNewComment("");
    setCommentType("external");
    setSelectedFile(null);
    setFilePreview(null);
    setIsCommentModalOpen(false);
    setSnackbarMessage("✅ Comment added successfully!");
    setSnackbarOpen(true);
  } catch (err) {
    console.error("Error adding comment:", err);
    alert("Failed to add comment.");
  }
};


useEffect(() => {
  const fetchUnresolved = async () => {
    if (!member?.MemberID && !workOrderDetails?.property_id) return;

    try {
      const memberWorkOrdersResp = await api.get(`/workorders/member/${workOrderDetails.property_id}`);
const unresolvedData = memberWorkOrdersResp.data.filter(
  (wo) => wo.status_id !== 4 // exclude Resolved
);
setUnresolvedTickets(unresolvedData);
    } catch (err) {
      console.error("❌ Failed to fetch unresolved tickets:", err);
    }
  };

  fetchUnresolved();
}, [workOrderDetails?.property_id, member]);

useEffect(() => {
  const fetchMembers = async () => {
    try {
      const res = await api.get("/membersfilter");
      setAvailableProperties(res.data.members || []); // ✅ use res.data.members
      console.log("✅ Loaded members:", res.data.members.length);
    } catch (err) {
      console.error("❌ Failed to fetch members:", err);
    }
  };
  fetchMembers();
}, []);

const handlePropertyChange = async (event) => {
  const newPropertyId = event.target.value;
  try {
    await api.put(`/editWorkOrder/${workOrderDetails.order_id}`, {
      property_id: newPropertyId,
      assign_to: workOrderDetails.assign_to,
      status: workOrderDetails.status,
      priority: workOrderDetails.priority,
    });

    setWorkOrderDetails((prev) => ({
      ...prev,
      property_id: newPropertyId,
    }));

    setSnackbarMessage("✅ Property updated successfully!");
    setSnackbarOpen(true);
  } catch (err) {
    console.error("❌ Failed to update property:", err);
    setSnackbarMessage("❌ Failed to update property!");
    setSnackbarOpen(true);
  }
};


  const handlePrintWorkOrder = () => {
    const printContents = printRef.current.innerHTML;
  
    // Inline styles from WorkOrderPrintStyles.css (add more if needed)
    const styles = `
      <style>
        body {
          font-family: Arial, sans-serif;
          color: #333;
          margin: 40px;
        }
        .print-title {
          text-align: center;
          color: #0057b7;
          border-bottom: 2px solid #ccc;
          padding-bottom: 10px;
        }
        .workorder-details-horizontal {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
          gap: 10px 30px;
          margin-bottom: 25px;
          font-size: 15px;
        }
        .workorder-details-horizontal div {
          white-space: nowrap;
          line-height: 1.5;
        }
        blockquote {
          border-left: 4px solid #007bff;
          padding-left: 12px;
          font-style: italic;
          margin: 10px 0;
        }
        .divider {
          margin: 30px 0;
          border: none;
          border-top: 1px solid #ccc;
        }
        .comments-section h3 {
          color: #007bff;
          margin-bottom: 10px;
        }
        .comment-block {
          margin-bottom: 12px;
          page-break-inside: avoid;
        }
        .comment-date {
          color: gray;
          margin-left: 10px;
          font-size: 0.9em;
        }
        .print-footer {
          text-align: center;
          font-size: 12px;
          color: #666;
          margin-top: 60px;
        }
          .comment-block {
  border-bottom: 1px solid #ddd;
  padding-bottom: 10px;
  margin-bottom: 15px;
  page-break-inside: avoid;
}

.comment-header {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  font-size: 14px;
}

.comment-author {
  color: #333;
}

.comment-date {
  font-size: 0.85em;
  color: gray;
  margin-left: 15px;
}

.comment-body {
  margin-top: 5px;
  padding-left: 12px;
  border-left: 3px solid #007bff;
  font-style: italic;
  color: #444;
}


      </style>
    `;
  
    const printWindow = window.open("", "", "width=900,height=700");
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Work Order #${workOrderDetails?.order_id}</title>
          ${styles}
        </head>
        <body>${printContents}</body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    printWindow.close();
  };
  
  if (isLoading) return <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}><CircularProgress /></Box>;
  if (error) return <Box sx={{ textAlign: "center", mt: 4, color: "red" }}><p>{error}</p></Box>;

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ p: 3 }}>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/home/workorders", { state: { reload: true } })}
          sx={{ mb: 3 }}
        >Back</Button>

        <Grid container spacing={3}>
        {member && (
 <Grid
  item
  xs={12}
  md={4}
  key={`profile-${member?.MemberID || member?.ClubMemberCode || workOrderDetails?.property_id || Date.now()}`}
>
  <MemberProfileCard
    key={`member-${member?.MemberID || member?.ClubMemberCode || workOrderDetails?.property_id || Date.now()}`}
    member={member}
    unresolvedTickets={unresolvedTickets.filter(
      (t) => String(t.order_id) !== String(id)
    )}
  />
  <Box sx={{ mt: 4 }}>
    <WorkOrderTimeline
      key={`timeline-${workOrderDetails?.property_id || Date.now()}`}
      workOrderId={workOrderDetails.order_id}
    />
  </Box>
</Grid>

)}



          <Grid item xs={12} md={8}>
            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
  <Button
    variant="contained"
    color="primary"
    onClick={() => setOpenNewTicketModal(true)}
    sx={{ mr: 2 }}
  >
    + New Ticket
  </Button>

  <Button
    variant="contained"
    color="primary"
    startIcon={<EmailIcon />}
    onClick={() => setEmailDialogOpen(true)}
  >
    Send Work Order via Email
  </Button>

  <Button
    variant="outlined"
    startIcon={<PrintIcon />}
    sx={{ ml: 2 }}
    onClick={handlePrintWorkOrder}
  >
    Print Work Order
  </Button>
</Box>

            <Tabs
              value={activeTab}
              onChange={(e, val) => setActiveTab(val)}
              indicatorColor="primary"
              textColor="primary"
              variant="scrollable"
              scrollButtons="auto"
              sx={{ mb: 2 }}
            >
              <Tab label="Details" />
              <Tab label="Calendar" />
              <Tab label="Comments" />
              <Tab label="Visits" />
            </Tabs>

            {activeTab === 0 && (
              <DetailsTab
                workOrderDetails={workOrderDetails}
                statuses={statuses}
                assignableUsers={assignableUsers}
                workOrderTypes={workOrderTypes}
                workOrderSubtypes={workOrderSubtypes}
                workOrderLocations={workOrderLocations}
                selectedTypeId={selectedTypeId}
                selectedSubtypeId={selectedSubtypeId}
                selectedLocationId={selectedLocationId}
                selectedPriority={selectedPriority}
                selectedAssignee={selectedAssignee}
                priorityLevels={priorityLevels}
                onTypeChange={handleTypeChange}
                onSubtypeChange={handleSubtypeChange}
                onLocationChange={handleLocationChange}
                onAssigneeChange={handleAssigneeChange}
                onPriorityChange={handlePriorityChange}
                calculateDaysOpen={calculateDaysOpen}
                setSnackbarMessage={setSnackbarMessage}
                setSnackbarOpen={setSnackbarOpen}
                setWorkOrderDetails={setWorkOrderDetails}
                availableProperties={availableProperties}
                handlePropertyChange={handlePropertyChange}
                imageURL={imageURL}
                onPropertyChanged={handlePropertyChanged}
              />
              
            )}
            {activeTab === 1 && (
              <CalendarTab workOrderId={id} currentUserId={currentUserId} events={[]} users={users} setEvents={() => {}} />
            )}
            {activeTab === 2 && (
              <CommentsTab comments={comments} onAddCommentClick={() => setIsCommentModalOpen(true)} onImageClick={(url) => {
                setCommentImageURL(url);
                setOpenCommentImageModal(true);
              }} apiBaseUrl={API_BASE_URL} />
            )}
            {activeTab === 3 && <VisitsTab work_order_id={id} currentUserId={currentUserId} />}
          </Grid>
        </Grid>

        <CommentModal
          open={isCommentModalOpen}
          onClose={() => setIsCommentModalOpen(false)}
          commentType={commentType}
          setCommentType={setCommentType}
          newComment={newComment}
          setNewComment={setNewComment}
          selectedFile={selectedFile}
          setSelectedFile={setSelectedFile}
          filePreview={filePreview}
          setFilePreview={setFilePreview}
          onSave={handleAddComment}
          workorderId={id}                     
          uploadedBy={currentUserId}  
          onCommentAdded={fetchComments}

        />

        <ImageModal
          open={openCommentImageModal}
          imageURL={commentImageURL}
          onClose={() => setOpenCommentImageModal(false)}
        />

        <Snackbar
          open={snackbarOpen}
          autoHideDuration={4000}
          onClose={() => setSnackbarOpen(false)}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert onClose={() => setSnackbarOpen(false)} severity="success" sx={{ width: "100%" }}>
            {snackbarMessage}
          </Alert>
        </Snackbar>

        <div style={{ display: "none" }}>
          <div ref={printRef}>
            <WorkOrderPrintContent
              workOrderDetails={workOrderDetails}
              selectedAssignee={selectedAssignee}
              selectedPriority={selectedPriority}
              selectedTypeId={selectedTypeId}
              selectedSubtypeId={selectedSubtypeId}
              selectedLocationId={selectedLocationId}
              workOrderTypes={workOrderTypes}
              workOrderSubtypes={workOrderSubtypes}
              workOrderLocations={workOrderLocations}
              comments={comments}
              member={member}
            />
          </div>
        </div>
       <Dialog
  open={openNewTicketModal}
  onClose={() => setOpenNewTicketModal(false)}
  fullWidth
  maxWidth="md"
>
  
  <DialogContent dividers>
    <AddWorkOrderSmart
      propertyId={workOrderDetails?.property_id}     // ✅ prefill property
      openedByUserId={currentUserId}                 // ✅ logged in user
      uploadedBy={currentUserId}                     // optional: same user for file upload
    />
  </DialogContent>
</Dialog>


        <Dialog open={emailDialogOpen} onClose={() => setEmailDialogOpen(false)}>
          <DialogTitle>Send Work Order via Email</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="Recipient Email"
              fullWidth
              value={recipientEmail}
              onChange={(e) => setRecipientEmail(e.target.value)}
              type="email"
            />
            <TextField
              margin="dense"
              label="CC Email (optional)"
              fullWidth
              value={ccEmail}
              onChange={(e) => setCcEmail(e.target.value)}
              type="email"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEmailDialogOpen(false)}>Cancel</Button>
            <Button onClick={async () => {
              setIsSending(true);
              try {
                await api.post("/send-workorder-email", {
                  workOrderId: id,
                  recipientEmail,
                  ccEmail: ccEmail || null,
                });
                setSnackbarMessage("✅ Email sent successfully.");
                setSnackbarOpen(true);
                setEmailDialogOpen(false);
              } catch (err) {
                setSnackbarMessage("❌ Failed to send email.");
                setSnackbarOpen(true);
                console.error("Send email error:", err);
              } finally {
                setIsSending(false);
              }
            }} disabled={isSending || !recipientEmail}>
              {isSending ? "Sending..." : "Send"}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
};

export default WorkOrdersDetails;
